<template>
  <PalettePicker 
    ref="dropdown"
    :toggleVariant_="'transparent-lightBlue'"
    :toggleClass_="'btn-square'"
    :boundary="'viewport'"
    :data="color" 
    @input="onInput"
    @show="onShow"
    @hide="onHide"
  >
    <template #icon>
      <BtnToggle 
        :title="$t('engine.manageText.modalWidget.tooltip.lblTextColor', languageEngine)"
      >
        <IconTextColor class="-mt-5" :style="`height: 16px; color: ${colorHex};`" v-show="Boolean(colorHexThreshold <= 0.98)" />
        <IconTextColorLight class="-mt-5" :style="`height: 16px; color: ${colorHex};`" v-show="Boolean(colorHexThreshold > 0.98)"/>
      </BtnToggle>
    </template>
  </PalettePicker>
</template>

<script>
import { mapGetters } from "vuex";
import PalettePicker from "@components_system/Shared/Tools/PalettePicker/PalettePicker";
import IconTextColor from "@components_system/Shared/Images/Icon/IconTextColor";
import IconTextColorLight from "@components_system/Shared/Images/Icon/IconTextColorLight";
import BtnToggle from "./_BtnToggle";
import Quill from "quill";

export default {
  components: {
    PalettePicker,
    IconTextColor,
    IconTextColorLight,
    BtnToggle,
  },
  data() {
    return {
      active: undefined,
    }
  },
  computed: {
    ...mapGetters({
      languageEngine: "designIframe/userPreference/languageEngine",
      texteditor: "designIframe/texteditor/texteditor",
      templateColor: "designIframe/metadata/templateColor",
    }),
    format() {
      return this.texteditor.selectionFormatMerge;
    },
    value() {
      if (Array.isArray(this.format.color)) return this.format.color[0];
      return this.format.color;
    },
    _value() {
      if (this.value && this.value.includes('rgba(')) return this.rgbaToColorAlpha(this.value); // rgba(var(--tpc-11), 100%), rgba(0, 0, 0, 100%)
      if (this.value && this.value.includes('rgb(')) return { color: this.rgbToColor(this.value), alpha: 100 }; // rgb(var(--tpc-11)), rgb(0, 0, 0)
      if (this.value && this.value.includes('#')) return { color: this.value.toUpperCase(), alpha: 100 }; // #000000
      return { 
        color: "#000000", 
        alpha: 100,
      };
    },
    color() {
      // --tpc-11
      // #000000
      return this._value.color;
    },
    colorHex() {
      if (this._value.color) {
        return this.colorToHex(this._value.color);
      }
      return this._value.color;
    },
    colorHexThreshold() {
      return this.$helper.color.hexThreshold(this.colorHex);
    },
  },
  methods: {
    rgbaToColorAlpha(value) {
      // rgba(var(--tpc-11), 100%)
      // rgba(var(--tpc-11), 1)
      // rgba(0, 0, 0, 100%)
      // rgba(0, 0, 0, 1)
      value = value.trim().replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, '');
      let valueArr = value.split(',').map(str => str.trim());
      if (value.includes('--tpc')) {
        return {
          color: valueArr[0].replace('var(', '').replace(')', ''), // --tpc-11
          alpha: this.alphaToPercent(valueArr[1]),
        }
      } else {
        return {
          color: this.colorToHex(valueArr.slice(0, 3).join(",")).toUpperCase(), // #000000
          alpha: this.alphaToPercent(valueArr[3]),
        }
      }
    },
    rgbToColor(value) {
      // rgb(var(--tpc-11))
      // rgb(0, 0, 0)
      value = value.trim().replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, '');
      let valueArr = value.split(',').map(str => str.trim());
      if (value.includes('--tpc')) {
        return valueArr[0].replace('var(', '').replace(')', ''); // --tpc-11
      } else {
        return this.colorToHex(valueArr.slice(0, 3).join(",")).toUpperCase(); // #000000
      }
    },

    colorToHex(value) {
      if (value) {
        value = value.trim();

        if (value.startsWith("#")) { // #xxxxxx
          return value.toUpperCase();

        } else if (value.startsWith("--tpc-")) { // --tpc-15
          return this.tcpToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgba(var(--tpc-")) {  // rgba(var(--tpc-15), 100%)
          return this.rgbaTpcToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgb(var(--tpc-")) {  // rgb(var(--tpc-15))
          return this.rgbTpcToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgba(")) {  // rgba(x,x,x,100%)
          return this.rgbaToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgb(")) {  // rgb(x,x,x)
          return this.rgbToHex(value).toUpperCase();
          
        } else if (value.match(/\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}/)) {  // x,x,x
          let arrRGB = value.split(',').map(str => str.trim());
          return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
        }
      }
      return value;
    },
    tcpToHex(value) {
      // --tpc-15
      if (value) {
        let tpc = value.trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbaTpcToHex(value) {
      // rgba(var(--tpc-15), 100%)
      if (value) {
        value = value.replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, ''); // var(--tpc-15), 100%
        value = value.replace('var(', '').replace(')', ''); // --tpc-15, 100%
        let arrValue = value.split(',').map(str => str.trim()); // [--tpc-15, 100%]
        let tpc = arrValue[0].trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbTpcToHex(value) {
      // rgb(var(--tpc-15))
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, ''); // var(--tpc-15)
        value = value.replace('var(', '').replace(')', ''); // --tpc-15
        let tpc = value.trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbaToHex(value) {
      // rgba(x,x,x,100%)
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, ''); // x,x,x,100%
        let arrValue = value.split(',').map(str => str.trim()); // [x,x,x,100%]
        let arrRGB = arrValue.slice(0, 3); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbToHex(value) {
      // rgb(x,x,x)
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, ''); // x,x,x
        let arrValue = value.split(',').map(str => str.trim()); // [x,x,x]
        let arrRGB = arrValue.slice(0, 3); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },

    alphaToPercent(value) {
      if (value === undefined || value === null) return 100;
      value = value.toString().trim();
      if (value.includes('%')) { // 50% => 50
        value = value.replaceAll('%', ''); 
        value = Math.min(Math.max(value, 0), 100);
        return value;
      }
      value = Number(value) * 100;
      value = Math.min(Math.max(value, 0), 100);
      return value;
    },

    onShow() {
      this.$store.dispatch("designIframe/texteditor/setData", { selection: this.texteditor.editor.getSelection() });
      this.$store.dispatch("designIframe/texteditor/setData", { colorpicker: true });
    },
    onHide() {
      this.$store.dispatch("designIframe/texteditor/setData", { selection: undefined });
      this.$store.dispatch("designIframe/texteditor/setData", { colorpicker: undefined });
      this.texteditor.editor.focus();
    },
    onInput(value) {
      const selection = this.texteditor.selection;
      let formatValue = this.$helper.color.getColor(value);
      // Convertion
      // --tpc-45 => rgba(var(--tpc-45), 100%)
      // #000000 => rgb(0, 0, 0)

      this.texteditor.editor.format('color', formatValue); 
      this.setFormatLineBlank(selection, formatValue);
      this.setFormatListItem(selection);

      this.$refs.dropdown.show(); // continue show
    },
    setFormatLineBlank(selection, formatValue) {
      this.texteditor.editor.formatText({index: selection.index + selection.length, length: 0}, { color: formatValue });
    },
    setFormatListItem(selection) {
      const ListItem = Quill.import("formats/list/item");
      let [line1, offset1] = this.texteditor.editor.getLine(selection.index); // first line
      let lines = this.texteditor.editor.getLines(selection);
      if (!lines.length) {
        lines = [line1];
      }

      if (lines.length) {
        lines.forEach((line, i) => {
          let lineLength = line.length(); // with br
          let lineIndex = this.texteditor.editor.getIndex(line);
          let lineOffset = (i == 0) ? offset1 : 0;
          let lineSelection = {
            index: lineIndex + lineOffset,
            length: lineLength - lineOffset,
          };

          if (line instanceof ListItem) {
            if (line.children.length === 1) { // same color in line only
              const child = line.children.head;
              const attributes = child.attributes && child.attributes.attributes;

              if (attributes && attributes.color) {
                const color = attributes.color.value(child.domNode);
                this.texteditor.editor.formatLine(lineSelection, { 
                  'lineColor': color, 
                  'listItemColor': color,
                });
              }
            }
          } else { // block other
            this.texteditor.editor.formatLine(lineSelection, { 
              'lineColor': null, 
              'listItemColor': null,
            });
          }
        });
      }
    }
  },
  mounted() {
    this.active = this.colorHex;
  },
  watch: {
    colorHex() {
      this.active = this.colorHex;
    }
  },
}
</script>
